import { Invoice, ServiceTransaction } from '../types';
import { FlatCheckProject, ValidationMessage, flatRateIssues, missingUsedIssues, performedSpanFromLog } from './invoiceLogic';
import {
    TorKopf,
    TorMeldung,
    abbildbareLeistungen,
    kappeStack,
    meldeUeberHintergrundkanal,
    sicherMelden,
    torMeldungsTitel,
} from './allocationGateShadow';
import { zahl } from './allocationShadow';
import { meldeKontingent } from './entwurfAusJournal';
import { bookedByReference, isAlreadyBooked } from './invoiceWriteBackLogic';
import {
    BilledStand,
    SplitIssue,
    altCoveredByParty,
    billedByParty,
    billedFor,
    unassignedBilledIssues,
} from './projectSplit';
import { ServiceJournalRow, journalToServiceTransactions } from './serviceJournal';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE TOR-LESER IM SCHATTEN (S5a Stufe 3c, Paket T) — OHNE WIRKUNG
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Seit Stufe 3b friert das Tor die Zuordnung aus dem Buchungsjournal ein
 * (allocationGate.ts). `planProjectIssue` (invoiceService) liest das Protokoll
 * aber noch an weiteren Stellen, und die lesen weiter das Projekt-JSON:
 *   - `billedByParty` — der Stand je Teilnehmer einer Aufteilung; kumulativ
 *     wird davon der Zuwachs abgezogen, und er geht als `billedForCustomer`
 *     in die Buchung (`aufteilung_weicht_ab`),
 *   - `isAlreadyBooked` — Idempotenz-Schicht 3 (`buchung_nur_im_journal`,
 *     siehe unten),
 *   - `performedSpanFromLog` — der Leistungszeitraum des Belegs
 *     (`leistungszeitraum_weicht_ab`),
 *   - `flatRateIssues` — die Pauschalriegel (`pauschalriegel_weicht_ab`),
 *   - `missingUsedIssues` — erbracht, aber nicht im Leistungsstand
 *     (`fehlstand_weicht_ab`),
 *   - `planWriteBack` mit `reverseOf`, also `bookedByReference` beim Storno
 *     (`storno_menge_weicht_ab`).
 * `componentLineIssues` (Paketriegel) liest das Protokoll NICHT — nur LV und
 * Belegzeilen; es gibt dort nichts zu beschatten.
 *
 * Controller-Entscheid 3c: diese Leser schalten NICHT um. Im Journal-Modus
 * rechnet dieses Modul sie zusaetzlich auf dem Journal und vergleicht mit den
 * JSON-Werten, die TATSAECHLICH verwendet wurden; es meldet nur. Umgeschaltet
 * wird erst in Stufe 3d — nach einer Woche Messung und dem Go des Betreibers.
 *
 * ─── Wie „ohne Wirkung" gesichert ist ──────────────────────────────────────
 *   - Die JSON-Werte haelt `planProjectIssue` im synchronen Teil fest, in dem
 *     Moment, in dem es sie selbst rechnet (`notiere` — wie `vermesseSeite`
 *     am Tor): das Buchungsart-Register ist global und veraenderlich, und bis
 *     das Journal da ist, kann ein Tab ein Projekt oeffnen oder schliessen.
 *     Festgehalten werden Ergebnisse und Kopien der Eingaben, nie Verweise,
 *     an denen die Ausstellung weiterarbeitet.
 *   - Die Journal-Seite rechnet erst, wenn das Journal da ist — das schon
 *     angeforderte (`journalVorab`), es wird kein zweites Mal geladen.
 *   - `starteTorLeserSchatten` laeuft ohne `await`, wirft nie, und jeder
 *     Fehler im Schatten wird gemeldet und geschluckt. Die Ausstellung liest
 *     nichts von hier zurueck: `lines` und `newEntries` sind dieselben wie
 *     ohne Schatten (Test in invoiceService.issue.test.ts).
 *   - Im JSON-Modus (Notschalter AN) laeuft er nicht.
 *
 * ─── `isAlreadyBooked` bleibt auf dem JSON, bis S9 ─────────────────────────
 * Die eigene erste Buchung steht im JSON; ihre Zweitschrift ins Journal folgt
 * erst nach dem fertigen Beleg. Aus dem Journal gelesen, buchte ein
 * Wiederholungsversuch doppelt. Deshalb:
 *   - JSON ja, Journal nein: der erwartete Fall einer Wiederholung — nur
 *     GEZAEHLT (`torLeserZaehler`), nie gemeldet.
 *   - Journal ja, JSON nein: ein aelterer Tab hat das JSON ueberschrieben,
 *     und die Ausstellung bucht ein ZWEITES Mal. Eigene Art und eigene
 *     Meldung („doppelte Buchung droht"), am eigenen Kontingent vorbei.
 *
 * ─── Was nach bug_reports geht ─────────────────────────────────────────────
 * Titel „[S5a-Tor] Tor-Leser im Schatten: … — Beleg …, Projekt …", erste
 * Stackzeile „S5a Stufe 3c, Tor-Leser im Schatten — OHNE Wirkung: …", danach
 * „Zeitraum: …" und „Arten: 1x …, … (N Abweichungen; …)" wie am Tor — die
 * Ueberwachung zaehlt mit denselben Mustern. Ist das Journal nicht lesbar,
 * meldet hier niemand: das Tor (oder bei durchgereichten Belegen der Schatten
 * aus 3b) hat es schon gemeldet; gezaehlt wird es trotzdem.
 */

/** Wie in allocationLogic: darunter ist eine Menge Rundungsrauschen. */
const EPS = 0.0005;

const weichtAb = (x: number, y: number): boolean => !(Math.abs(Number(x) - Number(y)) <= EPS);

/** Hoechstens so viele Abweichungen ausgeschrieben; der Rest wird gezaehlt. */
const MAX_ZEILEN = 20;

/** Eigenes Kontingent je Sitzung vor dem gemeinsamen Deckel des Kanals. */
export const MAX_TOR_LESER_MELDUNGEN = 8;

export type TorLeserArt =
    | 'aufteilung_weicht_ab'
    | 'buchung_nur_im_journal'
    | 'leistungszeitraum_weicht_ab'
    | 'pauschalriegel_weicht_ab'
    | 'fehlstand_weicht_ab'
    | 'storno_menge_weicht_ab'
    // createSplitInvoiceDrafts (Abschnitt „Die Aufteilung im Schatten")
    | 'teilung_offen_weicht_ab'
    | 'teilung_stand_weicht_ab'
    | 'teilung_alt_weicht_ab';

const ARTEN: TorLeserArt[] = [
    'buchung_nur_im_journal',
    'aufteilung_weicht_ab',
    'storno_menge_weicht_ab',
    'pauschalriegel_weicht_ab',
    'fehlstand_weicht_ab',
    'leistungszeitraum_weicht_ab',
    'teilung_offen_weicht_ab',
    'teilung_stand_weicht_ab',
    'teilung_alt_weicht_ab',
];

export interface TorLeserDiff {
    art: TorLeserArt;
    serviceId?: string;
    /** Wert aus dem JSON (verwendet) bzw. aus dem Journal (Schatten), lesbar. */
    json: string;
    journal: string;
    text: string;
}

/**
 * Was `planProjectIssue` festhaelt — die JSON-Werte, die es verwendet hat,
 * und Kopien der Eingaben, mit denen die Journal-Seite dasselbe rechnet.
 * Ein Feld fehlt, wenn die Ausstellung den Leser nicht erreicht hat (Sperre,
 * Riegel, Wiederholung) — dann wird es auch nicht verglichen.
 */
export interface TorLeserAufnahme {
    kopf: TorKopf;
    bookingReference: string;
    /** Das Protokoll aus dem JSON, als Liste kopiert. */
    jsonLog: ServiceTransaction[];
    /** Das schon angeforderte Journal (`journalVorab`). */
    journal: Promise<ServiceJournalRow[] | null> | null;
    /** `billedByParty` → `billedFor` je Leistung der Belegzeilen. */
    aufteilung?: { legacy: Map<string, string>; partyId: string; json: Map<string, number> };
    bereitsGebucht?: boolean;
    leistungszeitraum?: { stichtag?: string; seit?: string; json: { from?: string; to?: string } };
    /** Eingaben der beiden Riegel, die das Protokoll lesen. */
    riegel?: { projectId: string; invoice: Invoice; stand: Omit<FlatCheckProject, 'serviceLog'> };
    pauschalriegel?: string[];
    fehlstand?: string[];
    storno?: { reverseOf: string; json: Map<string, number> };
    /** Ein Festhalten ist ausgefallen — gemeldet als Ausfall, nicht verglichen. */
    defekt?: { fehler: unknown };
}

export const torLeserAufnahme = (kopf: TorKopf, bookingReference: string): TorLeserAufnahme => ({
    kopf,
    bookingReference,
    jsonLog: [],
    journal: null,
});

/**
 * Haelt etwas fest — und wirft NIE: ein Fehler beim Festhalten markiert die
 * Aufnahme als defekt und laesst die Ausstellung weiterlaufen.
 */
export const notiere = (a: TorLeserAufnahme | null | undefined, was: (a: TorLeserAufnahme) => void): void => {
    if (!a) return;
    try {
        was(a);
    } catch (fehler) {
        if (!a.defekt) a.defekt = { fehler };
    }
};

/** Eine Riegelliste vergleichbar: Code und Text, sortiert. */
export const riegelListe = (issues: ValidationMessage[] | null | undefined): string[] =>
    (issues ?? []).map((i) => `${i.code}: ${i.message}`).sort();

// ═══════════════════════════════════════════════════════════════════════════
// Der Vergleich — rein
// ═══════════════════════════════════════════════════════════════════════════

const listenDiff = (
    art: 'pauschalriegel_weicht_ab' | 'fehlstand_weicht_ab',
    name: string,
    json: string[],
    journal: string[],
): TorLeserDiff | null => {
    const nurJson = json.filter((x) => !journal.includes(x));
    const nurJournal = journal.filter((x) => !json.includes(x));
    if (nurJson.length === 0 && nurJournal.length === 0) return null;
    const teile = [
        ...nurJson.map((x) => `nur laut JSON: ${x.slice(0, 200)}`),
        ...nurJournal.map((x) => `nur laut Journal: ${x.slice(0, 200)}`),
    ];
    return {
        art,
        json: String(json.length),
        journal: String(journal.length),
        text: `${name}: ${json.length} Befund(e) laut JSON gegen ${journal.length} laut Journal — ${teile.join(' | ')}${
            json.length > 0 && journal.length === 0
                ? ' — nach dem Umschalten liesse dieser Riegel den Beleg durch'
                : json.length === 0 && journal.length > 0
                  ? ' — nach dem Umschalten hielte dieser Riegel den Beleg an'
                  : ''
        }.`,
    };
};

/**
 * Rechnet die festgehaltenen Leser auf dem Journal nach und vergleicht.
 * `wiederholung`: JSON ja, Journal nein bei `isAlreadyBooked` — nur zu zaehlen.
 */
export const vergleicheTorLeser = (
    a: TorLeserAufnahme,
    journalLog: ServiceTransaction[],
): { diffs: TorLeserDiff[]; wiederholung: boolean } => {
    const diffs: TorLeserDiff[] = [];
    let wiederholung = false;

    if (a.bereitsGebucht !== undefined) {
        const imJournal = isAlreadyBooked(journalLog, a.bookingReference);
        if (imJournal && !a.bereitsGebucht) {
            const zeilen = journalLog.filter((t) => t.bookingReference === a.bookingReference);
            diffs.push({
                art: 'buchung_nur_im_journal',
                json: 'nein',
                journal: 'ja',
                text: `Die Buchungsreferenz ${a.bookingReference} steht im Buchungsjournal (${zeilen.length} Zeile(n): ${zeilen
                    .slice(0, 5)
                    .map((t) => `Leistung ${t.serviceId} ${zahl(Number(t.quantity) || 0)}`)
                    .join(', ')}), im Projekt-JSON nicht — ausgestellt wurde nach dem JSON und damit ein zweites Mal gebucht.`,
            });
        } else if (!imJournal && a.bereitsGebucht) {
            wiederholung = true;
        }
    }

    if (a.aufteilung) {
        const { legacy, partyId, json } = a.aufteilung;
        const stands = billedByParty(journalLog, legacy);
        for (const [serviceId, x] of [...json].sort(([p], [q]) => p.localeCompare(q))) {
            const y = billedFor(stands.get(serviceId), partyId);
            if (!weichtAb(x, y)) continue;
            diffs.push({
                art: 'aufteilung_weicht_ab',
                serviceId,
                json: zahl(x),
                journal: zahl(y),
                text: `Leistung ${serviceId}: abgerechneter Stand des Teilnehmers ${partyId} ${zahl(x)} laut JSON gegen ${zahl(y)} laut Journal (billedByParty) — kumulativ der Abzug vom Zuwachs, in jedem Fall der Stand, gegen den gebucht wird.`,
            });
        }
    }

    if (a.storno) {
        const { reverseOf, json } = a.storno;
        const y = bookedByReference(journalLog, reverseOf);
        for (const serviceId of [...new Set([...json.keys(), ...y.keys()])].sort()) {
            const mj = json.get(serviceId) ?? 0;
            const mn = y.get(serviceId) ?? 0;
            if (!weichtAb(mj, mn)) continue;
            diffs.push({
                art: 'storno_menge_weicht_ab',
                serviceId,
                json: zahl(mj),
                journal: zahl(mn),
                text: `Leistung ${serviceId}: der stornierte Beleg (${reverseOf}) hat laut JSON ${zahl(mj)} gebucht, laut Journal ${zahl(mn)} (bookedByReference) — so viel nimmt das Storno zurueck.`,
            });
        }
    }

    if (a.riegel) {
        const { projectId, invoice, stand } = a.riegel;
        const kontext = { [projectId]: { ...stand, serviceLog: journalLog } };
        if (a.pauschalriegel) {
            const d = listenDiff('pauschalriegel_weicht_ab', 'Pauschalriegel (flatRateIssues)', a.pauschalriegel, riegelListe(flatRateIssues(invoice, kontext)));
            if (d) diffs.push(d);
        }
        if (a.fehlstand) {
            const d = listenDiff('fehlstand_weicht_ab', 'Erbracht, aber nicht im Leistungsstand (missingUsedIssues)', a.fehlstand, riegelListe(missingUsedIssues(invoice, kontext)));
            if (d) diffs.push(d);
        }
    }

    if (a.leistungszeitraum) {
        const { stichtag, seit, json } = a.leistungszeitraum;
        const y = performedSpanFromLog(journalLog, stichtag, seit);
        if ((json.from ?? '') !== (y.from ?? '') || (json.to ?? '') !== (y.to ?? '')) {
            diffs.push({
                art: 'leistungszeitraum_weicht_ab',
                json: `${json.from ?? '…'} bis ${json.to ?? '…'}`,
                journal: `${y.from ?? '…'} bis ${y.to ?? '…'}`,
                text: `Leistungszeitraum des Belegs ${json.from ?? '…'} bis ${json.to ?? '…'} laut JSON gegen ${y.from ?? '…'} bis ${y.to ?? '…'} laut Journal (performedSpanFromLog, eingefroren nach details.performedFrom/-To).`,
            });
        }
    }

    diffs.sort((p, q) => ARTEN.indexOf(p.art) - ARTEN.indexOf(q.art));
    return { diffs, wiederholung };
};

// ═══════════════════════════════════════════════════════════════════════════
// Die Meldungen
// ═══════════════════════════════════════════════════════════════════════════

const ERSTE_ZEILE = 'S5a Stufe 3c, Tor-Leser im Schatten — OHNE Wirkung:';

const zeitraumZeile = (kopf: TorKopf): string => `Zeitraum: ${kopf.zeitraum.from ?? '…'} bis ${kopf.zeitraum.to ?? '…'}`;

const artenZeile = (diffs: TorLeserDiff[]): string =>
    ARTEN.filter((art) => diffs.some((d) => d.art === art))
        .map((art) => `${diffs.filter((d) => d.art === art).length}x ${art}`)
        .join(', ');

export const torLeserMeldung = (a: TorLeserAufnahme, diffs: TorLeserDiff[]): TorMeldung => ({
    message: torMeldungsTitel('Tor-Leser im Schatten: Leser weichen ab', a.kopf),
    stack: kappeStack(
        [
            `${ERSTE_ZEILE} ausgestellt wurde mit den Werten aus dem Projekt-JSON; dieselben Leser liefen zusaetzlich auf dem Buchungsjournal (umgeschaltet wird erst in Stufe 3d).`,
            zeitraumZeile(a.kopf),
            `Arten: ${artenZeile(diffs)} (${diffs.length} Abweichungen; gezaehlt, nicht gemeldet: ${zaehler.wiederholungOhneJournal} Wiederholung(en) mit Buchung nur im JSON)`,
            ...diffs.slice(0, MAX_ZEILEN).map((d) => `- [${d.art}] ${d.text}`),
            ...(diffs.length > MAX_ZEILEN ? [`… und ${diffs.length - MAX_ZEILEN} weitere`] : []),
            JSON.stringify({
                ...a.kopf,
                bookingReference: a.bookingReference,
                anzahl: diffs.length,
                arten: Object.fromEntries(ARTEN.map((art) => [art, diffs.filter((d) => d.art === art).length]).filter(([, n]) => n)),
            }),
        ].join('\n'),
    ),
});

export const doppelteBuchungMeldung = (a: TorLeserAufnahme, d: TorLeserDiff): TorMeldung => ({
    message: torMeldungsTitel('Tor-Leser im Schatten: Buchung nur im Journal — doppelte Buchung droht', a.kopf),
    stack: kappeStack(
        [
            `${ERSTE_ZEILE} DOPPELTE BUCHUNG DROHT — das Buchungsjournal kennt die Buchungsreferenz dieses Belegs, das Projekt-JSON nicht (vermutlich hat ein aelterer Tab das JSON ueberschrieben); gebucht wurde nach dem JSON.`,
            zeitraumZeile(a.kopf),
            'Arten: 1x buchung_nur_im_journal (1 Abweichungen)',
            `- [${d.art}] ${d.text}`,
            JSON.stringify({ ...a.kopf, bookingReference: a.bookingReference, art: d.art }),
        ].join('\n'),
    ),
});

export const torLeserAusfallMeldung = (a: TorLeserAufnahme, fehler: unknown): TorMeldung => {
    const e = (fehler ?? {}) as { message?: string; stack?: string };
    return {
        message: torMeldungsTitel('Tor-Leser im Schatten ausgefallen', a.kopf),
        stack: kappeStack(
            [
                `${ERSTE_ZEILE} der Schatten selbst ist ausgefallen; ausgestellt wurde unveraendert.`,
                `Fehler: ${String(e.message ?? fehler).slice(0, 300)}`,
                e.stack ?? '',
                JSON.stringify({ ...a.kopf, bookingReference: a.bookingReference }),
            ].join('\n'),
        ),
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// Zaehler, Kontingent und die Huelle — wirft nie
// ═══════════════════════════════════════════════════════════════════════════

const zaehler = { wiederholungOhneJournal: 0, nichtLesbar: 0, gedrosselt: 0 };

/** Was nur gezaehlt wird (Kopf). Ein Modul-Zaehler wie `serviceLogAccessStats`. */
export const torLeserZaehler = (): { wiederholungOhneJournal: number; nichtLesbar: number; gedrosselt: number } => ({
    ...zaehler,
});

const kontingent = meldeKontingent(MAX_TOR_LESER_MELDUNGEN);

/** Nur fuer Tests: Zaehler und Kontingent zuruecksetzen. */
export const resetTorLeserSchatten = (): void => {
    zaehler.wiederholungOhneJournal = 0;
    zaehler.nichtLesbar = 0;
    zaehler.gedrosselt = 0;
    kontingent.zuruecksetzen();
};

export interface TorLeserDeps {
    melde: (m: TorMeldung) => void | Promise<void>;
    jetzt: () => number;
}

export const STANDARD_TOR_LESER_DEPS: TorLeserDeps = {
    melde: (m) => meldeUeberHintergrundkanal(m),
    jetzt: () => Date.now(),
};

/** Ueber das eigene Kontingent; die doppelte Buchung geht daran vorbei. */
const melde = async (deps: TorLeserDeps, baue: () => TorMeldung, amKontingentVorbei = false): Promise<void> => {
    try {
        const m = baue();
        if (!amKontingentVorbei && !kontingent.darf(m, deps.jetzt())) {
            zaehler.gedrosselt += 1;
            return;
        }
        await sicherMelden(deps, m);
    } catch {
        // Die Meldung ist eine Zugabe: sie darf die Ausstellung nie beruehren.
    }
};

/**
 * Wartet auf das schon angeforderte Journal, vergleicht und meldet. Wirft
 * nie und lehnt nie ab.
 */
export const torLeserSchatten = async (a: TorLeserAufnahme, deps: TorLeserDeps = STANDARD_TOR_LESER_DEPS): Promise<void> => {
    try {
        if (!a?.journal) return;
        let rows: ServiceJournalRow[] | null = null;
        try {
            rows = await a.journal;
        } catch {
            rows = null;
        }
        // Nicht lesbar hat das Tor (bzw. der 3b-Schatten) schon gemeldet.
        if (!Array.isArray(rows) || (rows.length === 0 && abbildbareLeistungen(a.jsonLog) > 0)) {
            zaehler.nichtLesbar += 1;
            return;
        }
        if (a.defekt) {
            const f = a.defekt.fehler;
            await melde(deps, () => torLeserAusfallMeldung(a, f));
            return;
        }
        const { diffs, wiederholung } = vergleicheTorLeser(a, journalToServiceTransactions(rows));
        if (wiederholung) zaehler.wiederholungOhneJournal += 1;
        const doppelt = diffs.find((d) => d.art === 'buchung_nur_im_journal');
        if (doppelt) await melde(deps, () => doppelteBuchungMeldung(a, doppelt), true);
        const rest = diffs.filter((d) => d.art !== 'buchung_nur_im_journal');
        if (rest.length > 0) await melde(deps, () => torLeserMeldung(a, rest));
    } catch (fehler) {
        await melde(deps, () => torLeserAusfallMeldung(a, fehler));
    }
};

/**
 * Startet den Schatten ohne `await` — der Aufruf in `planProjectIssue`
 * (`finally`) wirft nie und verlaengert die Ausstellung nicht.
 */
export const starteTorLeserSchatten = (
    a: TorLeserAufnahme | null | undefined,
    deps: TorLeserDeps = STANDARD_TOR_LESER_DEPS,
): void => {
    try {
        if (!a?.journal) return;
        void torLeserSchatten(a, deps).catch(() => {});
    } catch {
        // Auch hier: nie nach aussen.
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// Die Aufteilung im Schatten (createSplitInvoiceDrafts)
// ═══════════════════════════════════════════════════════════════════════════
//
// `createSplitInvoiceDrafts` (invoiceService) legt die Teilrechnungen mit drei
// Groessen aus dem JSON an (bzw. aus `input.serviceLog`): den nicht
// zugeordneten Altbestand, der sperrt (`unassignedBilledIssues`), den Stand
// je Teilnehmer (`billedByParty`, kumulativ der Abzug beim Teilen) und die
// ALT-Deckung je Teilnehmer (`altCoveredByParty`, previouslyInvoicedQty). Im
// Journal-Modus rechnet dieser Schatten alle drei auf dem Journal nach und
// meldet nur. Massgeblich bleibt das JSON. Anders als am Tor meldet hier
// niemand sonst ein nicht lesbares Journal — deshalb meldet es dieser
// Schatten selbst; sperren tut er nie.

type StandSchnitt = Map<string, Map<string, number>>;

const OHNE_TEILNEHMER = '(ohne Teilnehmer)';

/** Der Stand je Leistung und Teilnehmer als Kopie — `unassigned` unter „(ohne Teilnehmer)". */
export const standSchnitt = (stands: ReadonlyMap<string, BilledStand>, serviceIds: readonly string[]): StandSchnitt => {
    const out: StandSchnitt = new Map();
    for (const id of serviceIds) {
        const s = stands.get(id);
        const m = new Map<string, number>();
        for (const [p, q] of s?.byParty ?? []) m.set(p, q);
        if (s && Math.abs(s.unassigned) > EPS) m.set(OHNE_TEILNEHMER, s.unassigned);
        out.set(id, m);
    }
    return out;
};

export interface AufteilungsAufnahme {
    kopf: TorKopf;
    serviceIds: string[];
    legacy: Map<string, string>;
    jsonLog: ServiceTransaction[];
    /** Leistungen, an denen `unassignedBilledIssues` laut JSON sperrt. */
    offen: string[];
    stand: StandSchnitt;
    alt: StandSchnitt;
}

/** Festhalten im synchronen Teil — Kopien, keine Verweise. */
export const aufteilungsAufnahme = (e: {
    kopf: TorKopf;
    serviceIds: readonly string[];
    legacy: ReadonlyMap<string, string>;
    jsonLog: ServiceTransaction[];
    blockers: SplitIssue[];
    billedStands: ReadonlyMap<string, BilledStand>;
    altStands: ReadonlyMap<string, BilledStand>;
}): AufteilungsAufnahme => ({
    kopf: e.kopf,
    serviceIds: [...e.serviceIds],
    legacy: new Map(e.legacy),
    jsonLog: (e.jsonLog ?? []).slice(),
    offen: e.blockers.map((b) => b.serviceId ?? '').filter(Boolean).sort(),
    stand: standSchnitt(e.billedStands, e.serviceIds),
    alt: standSchnitt(e.altStands, e.serviceIds),
});

const schnittDiffs = (
    art: 'teilung_stand_weicht_ab' | 'teilung_alt_weicht_ab',
    name: string,
    json: StandSchnitt,
    journal: StandSchnitt,
): TorLeserDiff[] => {
    const out: TorLeserDiff[] = [];
    for (const serviceId of [...new Set([...json.keys(), ...journal.keys()])].sort()) {
        const a = json.get(serviceId) ?? new Map<string, number>();
        const b = journal.get(serviceId) ?? new Map<string, number>();
        for (const party of [...new Set([...a.keys(), ...b.keys()])].sort()) {
            const x = a.get(party) ?? 0;
            const y = b.get(party) ?? 0;
            if (!weichtAb(x, y)) continue;
            out.push({
                art,
                serviceId,
                json: zahl(x),
                journal: zahl(y),
                text: `Leistung ${serviceId}, Teilnehmer ${party}: ${name} ${zahl(x)} laut JSON gegen ${zahl(y)} laut Journal.`,
            });
        }
    }
    return out;
};

export const vergleicheAufteilung = (a: AufteilungsAufnahme, journalLog: ServiceTransaction[]): TorLeserDiff[] => {
    const diffs: TorLeserDiff[] = [];
    const offen = unassignedBilledIssues(a.serviceIds, journalLog, a.legacy)
        .map((b) => b.serviceId ?? '')
        .filter(Boolean)
        .sort();
    const nurJson = a.offen.filter((s) => !offen.includes(s));
    const nurJournal = offen.filter((s) => !a.offen.includes(s));
    if (nurJson.length || nurJournal.length) {
        diffs.push({
            art: 'teilung_offen_weicht_ab',
            json: a.offen.join(', ') || '—',
            journal: offen.join(', ') || '—',
            text: `Nicht zugeordneter Altbestand sperrt laut JSON an ${a.offen.join(', ') || 'keiner Leistung'}, laut Journal an ${offen.join(', ') || 'keiner Leistung'} (unassignedBilledIssues)${nurJson.length ? ' — nach dem Umschalten entstuenden die Teilrechnungen' : ' — nach dem Umschalten sperrte die Aufteilung'}.`,
        });
    }
    diffs.push(...schnittDiffs('teilung_stand_weicht_ab', 'abgerechneter Stand (billedByParty)', a.stand, standSchnitt(billedByParty(journalLog, a.legacy), a.serviceIds)));
    diffs.push(...schnittDiffs('teilung_alt_weicht_ab', 'ALT-Deckung (altCoveredByParty)', a.alt, standSchnitt(altCoveredByParty(journalLog, a.legacy), a.serviceIds)));
    return diffs;
};

const AUFTEILUNG_ERSTE_ZEILE = `${ERSTE_ZEILE} Teilrechnungen angelegt mit den Staenden aus dem Projekt-JSON (createSplitInvoiceDrafts)`;

export const aufteilungsMeldung = (a: AufteilungsAufnahme, diffs: TorLeserDiff[]): TorMeldung => ({
    message: torMeldungsTitel('Aufteilung im Schatten: Staende weichen ab', a.kopf),
    stack: kappeStack(
        [
            `${AUFTEILUNG_ERSTE_ZEILE}; dieselben drei Groessen liefen zusaetzlich auf dem Buchungsjournal.`,
            zeitraumZeile(a.kopf),
            `Arten: ${artenZeile(diffs)} (${diffs.length} Abweichungen)`,
            ...diffs.slice(0, MAX_ZEILEN).map((d) => `- [${d.art}] ${d.text}`),
            ...(diffs.length > MAX_ZEILEN ? [`… und ${diffs.length - MAX_ZEILEN} weitere`] : []),
            JSON.stringify({ ...a.kopf, serviceIds: a.serviceIds, anzahl: diffs.length }),
        ].join('\n'),
    ),
});

export const aufteilungNichtLesbarMeldung = (a: AufteilungsAufnahme, leerTrotz = 0): TorMeldung => ({
    message: torMeldungsTitel('Aufteilung im Schatten: Buchungsjournal nicht lesbar', a.kopf),
    stack: kappeStack(
        [
            `${AUFTEILUNG_ERSTE_ZEILE}; das Buchungsjournal war nicht lesbar — verglichen wurde nichts, gesperrt wird hier nie.`,
            leerTrotz > 0
                ? `loadJournal lieferte KEINE Zeile, das Projektprotokoll fuehrt aber ${leerTrotz} abbildbare Leistungen — vermutlich fehlt das Leserecht (service_journal: is_admin() or can_access_project), verglichen wurde nichts.`
                : 'loadJournal lieferte null (Tabelle fehlt, kein Netz oder keine Berechtigung) — verglichen wurde nichts.',
            JSON.stringify({ ...a.kopf, leerTrotz }),
        ].join('\n'),
    ),
});

export const aufteilungAusfallMeldung = (a: AufteilungsAufnahme, fehler: unknown): TorMeldung => {
    const e = (fehler ?? {}) as { message?: string; stack?: string };
    return {
        message: torMeldungsTitel('Aufteilung im Schatten ausgefallen', a.kopf),
        stack: kappeStack(
            [
                `${AUFTEILUNG_ERSTE_ZEILE}; der Schatten selbst ist ausgefallen.`,
                `Fehler: ${String(e.message ?? fehler).slice(0, 300)}`,
                e.stack ?? '',
                JSON.stringify(a.kopf),
            ].join('\n'),
        ),
    };
};

/**
 * Wartet auf Schalter und Journal (`'json'`: Notschalter AN — dann nichts),
 * vergleicht und meldet. Wirft nie und lehnt nie ab.
 */
export const aufteilungsSchatten = async (
    a: AufteilungsAufnahme,
    journal: Promise<ServiceJournalRow[] | null | 'json'>,
    deps: TorLeserDeps = STANDARD_TOR_LESER_DEPS,
): Promise<void> => {
    try {
        let rows: ServiceJournalRow[] | null | 'json' = null;
        try {
            rows = await journal;
        } catch {
            rows = null;
        }
        if (rows === 'json') return;
        if (!Array.isArray(rows)) {
            await melde(deps, () => aufteilungNichtLesbarMeldung(a));
            return;
        }
        const leistungen = abbildbareLeistungen(a.jsonLog);
        if (rows.length === 0 && leistungen > 0) {
            await melde(deps, () => aufteilungNichtLesbarMeldung(a, leistungen));
            return;
        }
        const diffs = vergleicheAufteilung(a, journalToServiceTransactions(rows));
        if (diffs.length > 0) await melde(deps, () => aufteilungsMeldung(a, diffs));
    } catch (fehler) {
        await melde(deps, () => aufteilungAusfallMeldung(a, fehler));
    }
};

/** Ohne `await` — der Aufruf in createSplitInvoiceDrafts wirft nie. */
export const starteAufteilungsSchatten = (
    baue: () => AufteilungsAufnahme,
    journal: Promise<ServiceJournalRow[] | null | 'json'>,
    deps: TorLeserDeps = STANDARD_TOR_LESER_DEPS,
): void => {
    try {
        const a = baue();
        void aufteilungsSchatten(a, journal, deps).catch(() => {});
    } catch {
        // Ein kaputtes Festhalten darf die Teilrechnungen nie beruehren.
    }
};
